import React, { useState, useEffect } from "react";
import axios from "../api/axios";
import { useAlert } from "../context/AlertContext";
import "./Goals.css";

const ICONS = ["🎯","⭐","🏆","💎","🚀","🎉","💜","🔥","👑","🎮","💰","🎁"];
const COLORS = ["#FFD700","#FFA500","#9147ff","#00f593","#ff6b6b","#4ecdc4","#ff8c00","#fff"];

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [subCount, setSubCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [form, setForm] = useState({ title:"", target:100, use_live_count:true, color:"#FFD700", icon:"🎯" });
  const notify = useAlert();

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [goalsRes, overlayRes] = await Promise.all([
        axios.get("/api/goals"),
        axios.get("/api/goals/overlay"),
      ]);
      setGoals(goalsRes.data);
      setSubCount(overlayRes.data.subCount || 0);
    } catch (e) { notify.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditGoal(null);
    setForm({ title:"", target:100, use_live_count:true, color:"#FFD700", icon:"🎯" });
    setShowForm(true);
  };

  const openEdit = (g) => {
    setEditGoal(g);
    setForm({ title:g.title, target:g.target, use_live_count:!!g.use_live_count, color:g.color||"#FFD700", icon:g.icon||"🎯" });
    setShowForm(true);
  };

  const save = async () => {
    try {
      if (editGoal) {
        await axios.put("/api/goals/" + editGoal.id, { ...form, reached: editGoal.reached });
      } else {
        await axios.post("/api/goals", form);
      }
      notify.success("Gespeichert!");
      setShowForm(false);
      loadAll();
    } catch (e) { notify.error("Fehler"); }
  };

  const del = async (id) => {
    try { await axios.delete("/api/goals/" + id); loadAll(); }
    catch (e) { notify.error("Fehler"); }
  };

  const overlayUrl = window.location.origin + "/overlay-subcounter.html";

  if (loading) return <div className="goals-page"><div className="goals-loading">Lade...</div></div>;

  return (
    <div className="goals-page">
      <div className="page-header">
        <div>
          <h2>Sub-Ziele</h2>
          <p>Setze Ziele für deinen Subscriber-Count mit Live-Progressbars im Overlay</p>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button className="btn-primary" onClick={() => { navigator.clipboard.writeText(overlayUrl); notify.success("URL kopiert!"); }}>
            📺 OBS URL kopieren
          </button>
          <button className="btn-primary" onClick={openCreate}>+ Ziel hinzufügen</button>
        </div>
      </div>

      {/* Current count */}
      <div className="goals-stat-bar">
        <div className="goals-stat">
          <span className="goals-stat-label">Aktuelle Subs</span>
          <span className="goals-stat-value">{subCount}</span>
        </div>
        <div className="goals-stat">
          <span className="goals-stat-label">Ziele gesamt</span>
          <span className="goals-stat-value">{goals.length}</span>
        </div>
        <div className="goals-stat">
          <span className="goals-stat-label">Erreicht</span>
          <span className="goals-stat-value">{goals.filter(g => (g.use_live_count ? subCount : g.current) >= g.target).length}</span>
        </div>
        <div className="goals-stat">
          <span className="goals-stat-label">Nächstes Ziel</span>
          <span className="goals-stat-value">
            {goals.filter(g => (g.use_live_count ? subCount : g.current) < g.target).sort((a,b) => a.target - b.target)[0]?.target || "—"}
          </span>
        </div>
      </div>

      {/* Goals list */}
      {goals.length === 0 ? (
        <div className="goals-empty">
          <div style={{fontSize:48}}>🎯</div>
          <p>Noch keine Ziele. Erstelle dein erstes Ziel!</p>
          <button className="btn-primary" onClick={openCreate}>Ziel erstellen</button>
        </div>
      ) : (
        <div className="goals-list">
          {[...goals].sort((a,b) => a.target - b.target).map(goal => {
            const effective = goal.use_live_count ? subCount : goal.current;
            const pct = Math.min(100, Math.round((effective / goal.target) * 100));
            const reached = effective >= goal.target;
            const remaining = Math.max(0, goal.target - effective);
            return (
              <div key={goal.id} className={"goal-card" + (reached ? " reached" : "")}>
                <div className="goal-card-header">
                  <div className="goal-card-title">
                    <span className="goal-card-icon">{goal.icon || "🎯"}</span>
                    <strong>{goal.title}</strong>
                    {reached && <span className="goal-badge">✓ Erreicht</span>}
                  </div>
                  <div className="goal-card-actions">
                    <button onClick={() => openEdit(goal)}>✏</button>
                    <button className="del" onClick={() => del(goal.id)}>🗑</button>
                  </div>
                </div>

                <div className="goal-card-progress">
                  <div className="goal-progress-track">
                    <div className="goal-progress-fill" style={{width: pct + "%", background: goal.color || "#FFD700"}} />
                  </div>
                  <div className="goal-progress-labels">
                    <span style={{color: goal.color || "#FFD700"}}>{effective} / {goal.target}</span>
                    <span>{pct}%{!reached && ` · noch ${remaining}`}</span>
                  </div>
                </div>

                <div className="goal-card-meta">
                  <span>{goal.use_live_count ? "🔴 Live-Count" : "📌 Manuell"}</span>
                  <span style={{color:"#555"}}>Erstellt: {new Date(goal.created_at).toLocaleDateString("de-DE")}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="goals-modal" onClick={e => e.stopPropagation()}>
            <h3>{editGoal ? "Ziel bearbeiten" : "Neues Ziel"}</h3>

            <div className="goals-form">
              <div className="goals-form-field">
                <label>Titel</label>
                <input type="text" value={form.title} onChange={e => setForm({...form, title:e.target.value})} placeholder="z.B. 100 Subs!" />
              </div>

              <div className="goals-form-field">
                <label>Ziel-Anzahl</label>
                <input type="number" value={form.target} onChange={e => setForm({...form, target:+e.target.value})} min="1" />
              </div>

              <div className="goals-form-field">
                <label>Icon</label>
                <div className="goals-icon-grid">
                  {ICONS.map(ic => (
                    <button key={ic} className={"goals-icon-btn" + (form.icon === ic ? " active" : "")} onClick={() => setForm({...form, icon:ic})}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>

              <div className="goals-form-field">
                <label>Farbe</label>
                <div className="goals-color-row">
                  {COLORS.map(c => (
                    <button key={c} className={"goals-color-btn" + (form.color === c ? " active" : "")}
                      style={{background:c}} onClick={() => setForm({...form, color:c})} />
                  ))}
                  <input type="color" value={form.color} onChange={e => setForm({...form, color:e.target.value})} />
                </div>
              </div>

              <div className="goals-form-field">
                <label className="goals-toggle-label">
                  <input type="checkbox" checked={form.use_live_count} onChange={e => setForm({...form, use_live_count:e.target.checked})} />
                  <span>Live Sub-Count verwenden (automatisch)</span>
                </label>
                <small>Wenn deaktiviert, kannst du den Fortschritt manuell setzen</small>
              </div>

              {!form.use_live_count && (
                <div className="goals-form-field">
                  <label>Aktueller Fortschritt</label>
                  <input type="number" value={form.current || 0} onChange={e => setForm({...form, current:+e.target.value})} min="0" />
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="goals-preview">
              <div className="goals-preview-label">Vorschau</div>
              <div className="goals-preview-item">
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                  <span style={{display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:700,color:"#ddd"}}>
                    <span>{form.icon}</span><span>{form.title || "Ziel-Titel"}</span>
                  </span>
                  <span style={{fontSize:12,color:"#888"}}>{subCount} / {form.target}</span>
                </div>
                <div style={{height:5,background:"rgba(255,255,255,0.08)",borderRadius:10,overflow:"hidden"}}>
                  <div style={{height:"100%",width:Math.min(100,Math.round((subCount/form.target)*100))+"%",background:form.color,borderRadius:10,transition:"width 0.5s"}} />
                </div>
              </div>
            </div>

            <div className="goals-modal-actions">
              <button onClick={() => setShowForm(false)}>Abbrechen</button>
              <button className="btn-primary" onClick={save}>💾 Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
